import { useMemo, useState } from "react";
import {
  App,
  AutoComplete,
  Button,
  Card,
  Empty,
  Flex,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import {
  ApplySanctionPayload,
  EventModerator,
  EventSanction,
  ModerationRole,
  SANCTION_DURATION_PRESETS,
  SanctionType,
  useAddEventModeratorMutation,
  useApplyEventSanctionMutation,
  useGetEventModerationQuery,
  useGetUsersQuery,
  useLiftEventSanctionMutation,
  useRemoveEventModeratorMutation,
} from "api";
import { getErrorMessage } from "components";

interface Props {
  eventId: number;
}

interface UserOption {
  value: string; // user id as string (AntD AutoComplete contract)
  label: string;
  userId: number;
}

/**
 * Moderation pane embedded inside EventEditPage.
 *
 * Layout: two cards stacked
 *   1. Moderatori — assign / remove + role select (admin vs moderator)
 *   2. Sancțiuni — list active bans/mutes + apply new + lift existing
 *
 * Search is debounced through AntD's AutoComplete; the dropdown shows
 * matches from `useGetUsersQuery({search, page: 1, perPage: 10})` so
 * we don't pull the whole user table just to pick one.
 */
export const EventModerationCard = ({ eventId }: Props) => {
  const { message } = App.useApp();
  const { data, isFetching } = useGetEventModerationQuery(eventId);

  // ------------------------------------------------------------------
  // Moderator search + add
  // ------------------------------------------------------------------
  const [modSearch, setModSearch] = useState("");
  const [pickedUserId, setPickedUserId] = useState<number | null>(null);
  const [pickedRole, setPickedRole] = useState<ModerationRole>("moderator");

  // Skip the user search until we have 2+ chars — keeps the dropdown
  // empty on mount and avoids hammering the API as the user clears it.
  const { data: userMatches } = useGetUsersQuery(
    { search: modSearch, page: 1, perPage: 10 },
    { skip: modSearch.trim().length < 2 },
  );

  const userOptions: UserOption[] = useMemo(() => {
    const rows = (userMatches as { data?: Array<Record<string, unknown>> })?.data ?? [];
    return rows.map((u) => {
      const id = Number(u.id);
      const username = String(u.username ?? "");
      const first = String(u.firstName ?? u.first_name ?? "");
      const last = String(u.lastName ?? u.last_name ?? "");
      const name = `${first} ${last}`.trim();
      return {
        value: String(id),
        label: name ? `${name} · @${username}` : `@${username}`,
        userId: id,
      };
    });
  }, [userMatches]);

  const [addModerator, { isLoading: adding }] = useAddEventModeratorMutation();
  const [removeModerator] = useRemoveEventModeratorMutation();
  const [applySanction] = useApplyEventSanctionMutation();
  const [liftSanction] = useLiftEventSanctionMutation();

  const handleAddModerator = async () => {
    if (pickedUserId === null) {
      message.warning("Caută și selectează un utilizator.");
      return;
    }
    try {
      await addModerator({
        eventId,
        userId: pickedUserId,
        role: pickedRole,
      }).unwrap();
      message.success("Moderator adăugat.");
      setModSearch("");
      setPickedUserId(null);
    } catch (e) {
      message.error(getErrorMessage(e as FetchBaseQueryError | SerializedError));
    }
  };

  // ------------------------------------------------------------------
  // Sanctions — apply new + lift existing
  // ------------------------------------------------------------------
  const [sanctionType, setSanctionType] = useState<SanctionType>("mute");
  const [sanctionDuration, setSanctionDuration] = useState<number | null>(3600);
  const [sanctionUserSearch, setSanctionUserSearch] = useState("");
  const [sanctionUserId, setSanctionUserId] = useState<number | null>(null);

  const { data: sanctionUserMatches } = useGetUsersQuery(
    { search: sanctionUserSearch, page: 1, perPage: 10 },
    { skip: sanctionUserSearch.trim().length < 2 },
  );

  const sanctionUserOptions: UserOption[] = useMemo(() => {
    const rows = (sanctionUserMatches as { data?: Array<Record<string, unknown>> })?.data ?? [];
    return rows.map((u) => {
      const id = Number(u.id);
      const username = String(u.username ?? "");
      const first = String(u.firstName ?? u.first_name ?? "");
      const last = String(u.lastName ?? u.last_name ?? "");
      const name = `${first} ${last}`.trim();
      return {
        value: String(id),
        label: name ? `${name} · @${username}` : `@${username}`,
        userId: id,
      };
    });
  }, [sanctionUserMatches]);

  const handleApplySanction = async () => {
    if (sanctionUserId === null) {
      message.warning("Caută și selectează un utilizator.");
      return;
    }
    try {
      const payload: ApplySanctionPayload = {
        userId: sanctionUserId,
        type: sanctionType,
        durationSeconds: sanctionDuration,
      };
      await applySanction({ eventId, ...payload }).unwrap();
      message.success(
        sanctionType === "ban" ? "Ban aplicat." : "Mute aplicat.",
      );
      setSanctionUserSearch("");
      setSanctionUserId(null);
    } catch (e) {
      message.error(getErrorMessage(e as FetchBaseQueryError | SerializedError));
    }
  };

  const moderatorColumns = [
    {
      title: "Utilizator",
      dataIndex: "username",
      render: (_: string, row: EventModerator) => {
        const name = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
        return (
          <Space direction="vertical" size={0}>
            {name && <Typography.Text strong>{name}</Typography.Text>}
            <Typography.Text type="secondary">
              @{row.username ?? `user-${row.userId}`}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "Rol",
      dataIndex: "role",
      width: 140,
      render: (role: ModerationRole) => (
        <Tag color={role === "admin" ? "magenta" : "blue"}>
          {role === "admin" ? "Admin" : "Moderator"}
        </Tag>
      ),
    },
    {
      title: "Asignat la",
      dataIndex: "assignedAt",
      width: 180,
      render: (v: string | null) =>
        v ? dayjs(v).format("DD.MM.YYYY HH:mm") : "—",
    },
    {
      title: "",
      width: 110,
      render: (_: unknown, row: EventModerator) => (
        <Popconfirm
          title="Elimini moderatorul?"
          okText="Da"
          cancelText="Nu"
          onConfirm={() =>
            removeModerator({ eventId, userId: row.userId })
              .unwrap()
              .then(() => message.success("Moderator eliminat."))
              .catch((e) => message.error(getErrorMessage(e)))
          }
        >
          <Button danger size="small">
            Elimină
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const sanctionColumns = [
    {
      title: "Utilizator",
      dataIndex: "username",
      render: (_: string, row: EventSanction) => {
        const name = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
        return (
          <Space direction="vertical" size={0}>
            {name && <Typography.Text strong>{name}</Typography.Text>}
            <Typography.Text type="secondary">
              @{row.username ?? `user-${row.userId}`}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: "Tip",
      dataIndex: "type",
      width: 110,
      render: (t: SanctionType) => (
        <Tag color={t === "ban" ? "red" : "orange"}>
          {t === "ban" ? "Ban" : "Mute"}
        </Tag>
      ),
    },
    {
      title: "Expiră",
      dataIndex: "expiresAt",
      width: 180,
      render: (v: string | null) =>
        v ? dayjs(v).format("DD.MM.YYYY HH:mm") : "Permanent",
    },
    {
      title: "Motiv",
      dataIndex: "reason",
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "",
      width: 110,
      render: (_: unknown, row: EventSanction) => (
        <Popconfirm
          title={`Anulezi ${row.type === "ban" ? "ban-ul" : "mute-ul"}?`}
          okText="Da"
          cancelText="Nu"
          onConfirm={() =>
            liftSanction({ eventId, userId: row.userId, type: row.type })
              .unwrap()
              .then(() => message.success("Sancțiune anulată."))
              .catch((e) => message.error(getErrorMessage(e)))
          }
        >
          <Button size="small">Anulează</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card title="Moderatori" loading={isFetching && !data}>
        <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
          <AutoComplete
            style={{ minWidth: 280, flex: 1 }}
            placeholder="Caută utilizator după nume sau username…"
            value={modSearch}
            options={userOptions}
            onSearch={(v) => {
              setModSearch(v);
              setPickedUserId(null);
            }}
            onSelect={(_v: string, option) => {
              setPickedUserId((option as unknown as UserOption).userId);
              setModSearch((option as unknown as UserOption).label);
            }}
          />
          <Select
            value={pickedRole}
            style={{ width: 140 }}
            onChange={(v) => setPickedRole(v as ModerationRole)}
            options={[
              { value: "moderator", label: "Moderator" },
              { value: "admin", label: "Admin" },
            ]}
          />
          <Button
            type="primary"
            onClick={handleAddModerator}
            loading={adding}
            disabled={pickedUserId === null}
          >
            Adaugă
          </Button>
        </Flex>

        <Table<EventModerator>
          dataSource={data?.moderators ?? []}
          rowKey="userId"
          columns={moderatorColumns}
          pagination={false}
          locale={{
            emptyText: <Empty description="Niciun moderator desemnat" />,
          }}
        />
      </Card>

      <Card title="Sancțiuni active" loading={isFetching && !data}>
        <Flex gap={8} wrap="wrap" style={{ marginBottom: 16 }}>
          <AutoComplete
            style={{ minWidth: 280, flex: 1 }}
            placeholder="Caută utilizator pentru a sancționa…"
            value={sanctionUserSearch}
            options={sanctionUserOptions}
            onSearch={(v) => {
              setSanctionUserSearch(v);
              setSanctionUserId(null);
            }}
            onSelect={(_v: string, option) => {
              setSanctionUserId((option as unknown as UserOption).userId);
              setSanctionUserSearch(
                (option as unknown as UserOption).label,
              );
            }}
          />
          <Select
            value={sanctionType}
            style={{ width: 110 }}
            onChange={(v) => setSanctionType(v as SanctionType)}
            options={[
              { value: "mute", label: "Mute" },
              { value: "ban", label: "Ban" },
            ]}
          />
          <Select
            value={String(sanctionDuration ?? "null")}
            style={{ width: 140 }}
            onChange={(v) =>
              setSanctionDuration(v === "null" ? null : Number(v))
            }
            options={SANCTION_DURATION_PRESETS.map((p) => ({
              value: String(p.seconds ?? "null"),
              label: p.label,
            }))}
          />
          <Button
            type="primary"
            danger={sanctionType === "ban"}
            onClick={handleApplySanction}
            disabled={sanctionUserId === null}
          >
            Aplică
          </Button>
        </Flex>

        <Table<EventSanction>
          dataSource={data?.sanctions ?? []}
          rowKey={(row) => `${row.userId}-${row.type}`}
          columns={sanctionColumns}
          pagination={false}
          locale={{
            emptyText: <Empty description="Nicio sancțiune activă" />,
          }}
        />
      </Card>
    </Space>
  );
};
